import { z } from 'zod';
import { MoneyNum, QtyNum } from './numbers';
import { MatchResult, PaymentInvoiceLineView } from './payment-control';

/* ───────────────────────── Этапы заявки на закуп (6.7, СП-01) ───────────────────────── */

export const PROCUREMENT_STAGES = {
  new: 'Новая',
  review: 'Проверка заявки',
  rfq: 'Сбор КП',
  analysis: 'Анализ КП',
  approval: 'Согласование выбора',
  contract: 'Договор или счёт',
  registry: 'Готово к реестру',
  payment: 'В реестре, оплата',
  delivery: 'Поставка',
  closed: 'Закрыта',
  returned: 'Возвращена',
  cancelled: 'Отменена',
} as const;
export type ProcurementStage = keyof typeof PROCUREMENT_STAGES;
export const ProcurementStageSchema = z.enum(
  Object.keys(PROCUREMENT_STAGES) as [ProcurementStage, ...ProcurementStage[]],
);

/** Порядок колонок доски заявок */
export const BOARD_STAGES: ProcurementStage[] = [
  'new',
  'review',
  'rfq',
  'analysis',
  'approval',
  'contract',
  'registry',
  'payment',
  'delivery',
];

/** Рабочих дней на этап до просрочки (правила контроля сроков) */
export const STAGE_SLA_DAYS: Partial<Record<ProcurementStage, number>> = {
  new: 2,
  review: 1,
  rfq: 5,
  analysis: 2,
  approval: 2,
  contract: 3,
  registry: 2,
  payment: 5,
  returned: 3,
};

/** Ключ настроек компании со сроками этапов: только отличия от STAGE_SLA_DAYS (ЗК-08) */
export const STAGE_SLA_KEY = 'procurement.stage_sla';
/** Этапы, у которых есть срок */
export const STAGE_SLA_STAGES = Object.keys(STAGE_SLA_DAYS) as ProcurementStage[];
export const StageSlaInput = z.object({
  days: z.partialRecord(ProcurementStageSchema, z.number().int().min(1).max(60)),
});
export type StageSlaInput = z.infer<typeof StageSlaInput>;
export const StageSlaView = z.record(z.string(), z.number().int());
export type StageSlaView = z.infer<typeof StageSlaView>;

/**
 * Переходы между этапами и право, нужное для перехода. Возврат и отмена — с причиной.
 * Сервер и интерфейс пользуются одной таблицей.
 */
export const STAGE_TRANSITIONS: Array<{
  from: ProcurementStage[];
  to: ProcurementStage;
  permission: string;
  label: string;
  needsComment?: boolean;
}> = [
  {
    from: ['new', 'returned'],
    to: 'review',
    permission: 'procurement.request.create',
    label: 'Отправить на проверку',
  },
  { from: ['review'], to: 'rfq', permission: 'procurement.request.review', label: 'Принять в работу' },
  {
    from: ['review'],
    to: 'returned',
    permission: 'procurement.request.review',
    label: 'Вернуть инициатору',
    needsComment: true,
  },
  { from: ['rfq'], to: 'analysis', permission: 'procurement.rfq.manage', label: 'КП собраны, к анализу' },
  {
    from: ['analysis'],
    to: 'rfq',
    permission: 'procurement.analysis',
    label: 'Запросить ещё КП',
    needsComment: true,
  },
  {
    from: ['analysis'],
    to: 'approval',
    permission: 'procurement.analysis',
    label: 'Выбор сделан, на согласование',
  },
  { from: ['approval'], to: 'contract', permission: 'procurement.approve.limit', label: 'Согласовать выбор' },
  {
    from: ['approval'],
    to: 'analysis',
    permission: 'procurement.approve.limit',
    label: 'Вернуть на анализ',
    needsComment: true,
  },
  {
    from: ['contract'],
    to: 'registry',
    permission: 'procurement.payment.request',
    label: 'Счёт получен, готово к реестру',
  },
  {
    from: ['payment'],
    to: 'delivery',
    permission: 'procurement.payment.confirm',
    label: 'Оплата завершена',
  },
  { from: ['delivery'], to: 'closed', permission: 'procurement.registry', label: 'Поставлено, закрыть' },
  {
    from: ['new', 'review', 'rfq', 'analysis', 'approval', 'returned'],
    to: 'cancelled',
    permission: 'procurement.request.review',
    label: 'Отменить заявку',
    needsComment: true,
  },
];

/** Двенадцать утверждённых статей расходов финансового плана (БМ-14, Регламент п. 3.6) */
export const COST_ARTICLES = [
  'Транспортные расходы (аренда авто, доставка)',
  'СМР, ПНР',
  'Производственные расходы (Ж/д, авиа., корочки. хранение обор-я, охрана)',
  'Проектирование',
  'Погашение процентов (вознаграждения) по БГ',
  'Оборудование',
  'Непредвиденные расходы по проекту',
  'Налоги и др. платежи в бюджет по проекту',
  'Материалы',
  'Командировочные расходы',
  'ГПХ по проекту',
  'Административные расходы по проекту',
] as const;

/** Критерии сравнения КП и веса шаблона категории (ЗК-21) */
export const SCORE_CRITERIA = {
  price: 'Цена с доставкой',
  term: 'Срок поставки',
  payment: 'Аванс и отсрочка',
  docs: 'Документы и надёжность',
  completeness: 'Полнота КП',
} as const;
export type ScoreCriterion = keyof typeof SCORE_CRITERIA;
export const DEFAULT_WEIGHTS: Record<ScoreCriterion, number> = {
  price: 50,
  term: 15,
  payment: 15,
  docs: 10,
  completeness: 10,
};
export const WeightsInput = z.object({
  weights: z.record(
    z.enum(Object.keys(SCORE_CRITERIA) as [ScoreCriterion, ...ScoreCriterion[]]),
    z.number().min(0).max(100),
  ),
  reason: z.string().trim().min(5, 'Обоснуйте изменение весов').max(2000),
});
export type WeightsInput = z.infer<typeof WeightsInput>;

/**
 * До этой суммы включительно сравнительные цены не нужны (приказ № 31П) — значение по умолчанию. Действующий
 * порог — в настройках компании («Администрирование → Настройки», ключ `procurement.threeOffers`, ЗК-05).
 */
export const NO_COMPARISON_LIMIT = 300_000;

export function requestNumber(n: number): string {
  return `ЗМ-${String(n).padStart(6, '0')}`;
}

/** РЗ-2026-0018 (ЗК-02) */
export function registryNumber(year: number, n: number): string {
  return `РЗ-${year}-${String(n).padStart(4, '0')}`;
}

/** ЗО-000001 (ЗК-02) */
export function paymentRequestNumber(n: number): string {
  return `ЗО-${String(n).padStart(6, '0')}`;
}

export const PAYMENT_KINDS = {
  advance: 'Аванс',
  delivery: 'Промежуточный: по поставке или акту',
  final: 'Окончательный расчёт',
  other: 'Прочее',
} as const;
export type PaymentKind = keyof typeof PAYMENT_KINDS;

/**
 * Документы для оплаты (ФН-19, ДГ-12): каталог видов, из которых собирается справочник комплектов.
 * Без полного комплекта заявка на оплату не уходит в 1С.
 */
export const PAYMENT_DOC_KINDS = {
  contract: 'Подписанный договор',
  advance_guarantee: 'Гарантия возврата аванса',
  invoice: 'Счёт на оплату',
  waybill: 'Накладная или СНТ',
  esf: 'ЭСФ',
  certificates: 'Сертификаты и паспорта качества',
  ks2: 'Акт выполненных работ (КС-2)',
  ks3: 'Справка о стоимости выполненных работ (КС-3)',
  hidden_works: 'Акты скрытых работ',
  asbuilt: 'Исполнительная документация на принятые объёмы',
  services_act: 'Акт оказанных услуг',
  reconciliation: 'Акт сверки',
  acceptance: 'Акт приёмки объекта',
  asbuilt_transfer: 'Передача исполнительной документации',
  basis: 'Документ-основание платежа: счёт, письмо или расчёт',
} as const;
export type PaymentDocKind = keyof typeof PAYMENT_DOC_KINDS;

/** Для какого договора комплект: «любой» действует, когда для вида договора своего комплекта нет */
export const DOC_RULE_CONTRACT_KINDS = {
  any: 'Любой договор',
  none: 'Без договора, по счёту',
  supply: 'Поставка',
  works: 'Подряд',
  subcontract: 'Субподряд',
  services: 'Услуги',
  rent: 'Аренда техники',
  framework: 'Рамочный',
} as const;
export type DocRuleContractKind = keyof typeof DOC_RULE_CONTRACT_KINDS;

export const DOC_RULE_CONDITIONS = {
  advance_guarantee: 'если договор требует гарантию возврата аванса',
} as const;
export type DocRuleCondition = keyof typeof DOC_RULE_CONDITIONS;

type DefaultRule = {
  contractKind: DocRuleContractKind;
  paymentKind: PaymentKind;
  key: PaymentDocKind;
  condition?: DocRuleCondition;
};
const set = (
  contractKinds: DocRuleContractKind[],
  paymentKinds: PaymentKind[],
  keys: Array<PaymentDocKind | [PaymentDocKind, DocRuleCondition]>,
): DefaultRule[] =>
  contractKinds.flatMap((contractKind) =>
    paymentKinds.flatMap((paymentKind) =>
      keys.map((k) =>
        Array.isArray(k)
          ? { contractKind, paymentKind, key: k[0], condition: k[1] }
          : { contractKind, paymentKind, key: k },
      ),
    ),
  );

/**
 * Комплекты по умолчанию — по ФН-19: аванс — подписанный договор и гарантия возврата аванса, если её
 * требует договор; поставка — накладная или СНТ, ЭСФ, сертификаты и паспорта качества; промежуточная
 * оплата подряда — КС-2 и КС-3, ЭСФ, акты скрытых работ и исполнительная; окончательная оплата
 * подряда — акт сверки, акт приёмки объекта и передача исполнительной. Справочник меняют финансовый
 * менеджер и администратор.
 */
export const DEFAULT_PAYMENT_DOC_RULES: DefaultRule[] = [
  ...set(['any'], ['advance'], ['contract', ['advance_guarantee', 'advance_guarantee']]),
  ...set(['none'], ['advance'], ['invoice']),
  ...set(['supply', 'framework', 'none'], ['delivery', 'final'], ['waybill', 'esf', 'certificates']),
  ...set(['works', 'subcontract'], ['delivery'], ['ks2', 'ks3', 'esf', 'hidden_works', 'asbuilt']),
  ...set(['works', 'subcontract'], ['final'], ['reconciliation', 'acceptance', 'asbuilt_transfer']),
  ...set(['services', 'rent'], ['delivery'], ['services_act', 'esf']),
  ...set(['services', 'rent'], ['final'], ['services_act', 'esf', 'reconciliation']),
  ...set(['any'], ['delivery'], ['esf']),
  ...set(['any'], ['final'], ['reconciliation']),
  // Прочий платёж тоже не уходит без основания: иначе смена вида заявки обходила бы комплект
  ...set(['any'], ['other'], ['basis']),
];

export const PaymentDocRuleView = z.object({
  id: z.uuid(),
  contractKind: z.enum(
    Object.keys(DOC_RULE_CONTRACT_KINDS) as [DocRuleContractKind, ...DocRuleContractKind[]],
  ),
  paymentKind: z.enum(Object.keys(PAYMENT_KINDS) as [PaymentKind, ...PaymentKind[]]),
  key: z.string(),
  title: z.string(),
  condition: z.enum(Object.keys(DOC_RULE_CONDITIONS) as [DocRuleCondition, ...DocRuleCondition[]]).nullable(),
  sortOrder: z.number().int(),
  active: z.boolean(),
});
export type PaymentDocRuleView = z.infer<typeof PaymentDocRuleView>;

export const PaymentDocRulePatch = z.object({
  title: z.string().trim().min(2).max(200).optional(),
  condition: z
    .enum(Object.keys(DOC_RULE_CONDITIONS) as [DocRuleCondition, ...DocRuleCondition[]])
    .nullable()
    .optional(),
  sortOrder: z.number().int().min(0).max(1000).optional(),
  active: z.boolean().optional(),
});
export type PaymentDocRulePatch = z.infer<typeof PaymentDocRulePatch>;

export const PaymentDocRulesResponse = z.object({
  rules: z.array(PaymentDocRuleView),
  /** Документ комплекта считается полученным, только когда приложен файл */
  requireFile: z.boolean(),
  can: z.object({ edit: z.boolean() }),
});
export type PaymentDocRulesResponse = z.infer<typeof PaymentDocRulesResponse>;

export const PaymentDocRuleInput = z.object({
  contractKind: z.enum(
    Object.keys(DOC_RULE_CONTRACT_KINDS) as [DocRuleContractKind, ...DocRuleContractKind[]],
  ),
  paymentKind: z.enum(Object.keys(PAYMENT_KINDS) as [PaymentKind, ...PaymentKind[]]),
  /** Вид документа из каталога или свой документ с названием */
  key: z
    .string()
    .trim()
    .regex(/^[a-z0-9_]{2,40}$/)
    .optional(),
  title: z.string().trim().min(2).max(200),
  condition: z
    .enum(Object.keys(DOC_RULE_CONDITIONS) as [DocRuleCondition, ...DocRuleCondition[]])
    .nullable()
    .optional(),
  sortOrder: z.number().int().min(0).max(1000).optional(),
});
export type PaymentDocRuleInput = z.infer<typeof PaymentDocRuleInput>;

/** Очерёдность платежа в платёжном календаре (ФН-02) */
export const PAYMENT_PRIORITIES = {
  high: 'Первоочередной',
  normal: 'Обычный',
  low: 'Можно перенести',
} as const;
export type PaymentPriority = keyof typeof PAYMENT_PRIORITIES;

/** Источник денег платежа (ФН-02, ФН-14) */
export const FUNDING_SOURCES = {
  project: 'Поступления по проекту',
  customer_advance: 'Аванс заказчика',
  own: 'Средства компании',
  credit: 'Кредит или овердрафт',
} as const;
export type FundingSource = keyof typeof FUNDING_SOURCES;

export const PAYMENT_REQUEST_STATUSES = {
  draft: 'Черновик',
  /** Прежние заявки сверх договора: после допсоглашения их передают снова или отменяют (ЗК-15) */
  approval: 'Сверх суммы договора',
  /** Остановлена до решения финансового менеджера: возможна повторная оплата (ФН-21) или сверх лимита (ФН-02) */
  hold: 'Остановлена',
  sent: 'Передана на оплату',
  paid: 'Оплачена',
  cancelled: 'Отменена',
} as const;
export type PaymentRequestStatus = keyof typeof PAYMENT_REQUEST_STATUSES;

export const REGISTRY_STATUSES = {
  draft: 'Формируется',
  sent: 'Передан на оплату',
} as const;
export type RegistryStatus = keyof typeof REGISTRY_STATUSES;

/* ───────────────────────── Заявка ───────────────────────── */

const Money = z.number().finite();
/** Ввод сумм — с пределом, чтобы ошибка ввода не превращалась в сбой сервера */
const MoneyIn = MoneyNum;
const DateStr = z.iso.date();

export const RequestLineInput = z.object({
  id: z.uuid().optional(),
  name: z.string().trim().min(2).max(500),
  unit: z.string().trim().min(1).max(30),
  quantity: QtyNum.positive('Количество больше нуля'),
  estimatePrice: MoneyIn.nonnegative().nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type RequestLineInput = z.infer<typeof RequestLineInput>;

export const PurchaseRequestInput = z.object({
  projectId: z.uuid(),
  title: z.string().trim().min(3).max(300),
  budgetLineId: z.uuid().nullable().optional(),
  /** Статья ФП: одна из утверждённых (БМ-14, БМ-15); проверяется на сервере */
  costArticle: z.string().trim().min(2).max(200).nullable().optional(),
  requiredDate: DateStr.nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  lines: z.array(RequestLineInput).min(1, 'Добавьте хотя бы одну позицию').max(500),
});
export type PurchaseRequestInput = z.infer<typeof PurchaseRequestInput>;

export const OfferExtra = z.object({
  name: z.string().trim().min(2).max(300),
  unit: z.string().trim().min(1).max(30),
  quantity: QtyNum.positive(),
  price: MoneyIn.nonnegative().nullable(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type OfferExtra = z.infer<typeof OfferExtra>;

export const OfferInput = z.object({
  supplierId: z.uuid().nullable().optional(),
  /** Новый поставщик, если его нет в реестре контрагентов */
  supplierName: z.string().trim().min(2).max(300).optional(),
  receivedOn: DateStr.nullable().optional(),
  validUntil: DateStr.nullable().optional(),
  deliveryIncluded: z.boolean().optional(),
  deliveryCost: MoneyIn.nonnegative().nullable().optional(),
  deliveryDays: z.number().int().nonnegative().max(3650).nullable().optional(),
  paymentTerms: z.string().trim().max(300).nullable().optional(),
  advancePct: z.number().min(0).max(100).nullable().optional(),
  deferralDays: z.number().int().min(0).max(365).nullable().optional(),
  documentsOk: z.boolean().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
  /** Цена за единицу с НДС по позиции заявки; null — позиции нет */
  prices: z.record(z.uuid(), MoneyIn.nonnegative().nullable()),
  /** Дополнительные позиции сверх запроса (ЗК-31): в сумму сравнения не входят */
  extras: z.array(OfferExtra).max(100).optional(),
});
export type OfferInput = z.infer<typeof OfferInput>;

export const TransitionInput = z.object({
  to: ProcurementStageSchema,
  comment: z.string().trim().max(2000).optional(),
});
export type TransitionInput = z.infer<typeof TransitionInput>;

export const SelectOfferInput = z.object({
  offerId: z.uuid().nullable(),
  reason: z.string().trim().max(2000).optional(),
  noThreeOffersReason: z.string().trim().max(2000).optional(),
});
export type SelectOfferInput = z.infer<typeof SelectOfferInput>;

export const PurchaseRequestSummary = z.object({
  id: z.uuid(),
  number: z.string(),
  title: z.string(),
  projectId: z.uuid(),
  projectCode: z.string(),
  projectName: z.string(),
  stage: ProcurementStageSchema,
  stageChangedAt: z.string(),
  daysOnStage: z.number().int(),
  overdue: z.boolean(),
  /** Рабочих дней на текущем этапе по настройкам компании; null — срок не задан */
  slaDays: z.number().int().nullable(),
  requiredDate: DateStr.nullable(),
  costArticle: z.string().nullable(),
  lines: z.number().int(),
  offers: z.number().int(),
  estimateTotal: Money.nullable(),
  bestOfferTotal: Money.nullable(),
  selectedSupplier: z.string().nullable(),
  selectedTotal: Money.nullable(),
  initiatorName: z.string().nullable(),
  buyerName: z.string().nullable(),
  registryId: z.uuid().nullable(),
  registryNumber: z.string().nullable(),
  /** Сумма к оплате: договор по заявке или выбранное КП */
  dueAmount: Money.nullable(),
  paidAmount: Money.nullable(),
  /** none — оплат нет, partial — частично оплачено, full — оплачено полностью (ЗК-07) */
  paymentStatus: z.enum(['none', 'partial', 'full']),
});
export type PurchaseRequestSummary = z.infer<typeof PurchaseRequestSummary>;

export const OfferView = z.object({
  id: z.uuid(),
  supplierId: z.uuid(),
  supplierName: z.string(),
  receivedOn: DateStr.nullable(),
  validUntil: DateStr.nullable(),
  deliveryIncluded: z.boolean(),
  deliveryCost: Money.nullable(),
  deliveryDays: z.number().int().nullable(),
  paymentTerms: z.string().nullable(),
  advancePct: z.number().nullable(),
  deferralDays: z.number().int().nullable(),
  /** Баллы 0–100 по критериям и итог с весами (ЗК-21); null — суммы скрыты или КП неполное */
  scores: z.record(z.string(), z.number()).nullable(),
  score: z.number().nullable(),
  isBestScore: z.boolean(),
  documentsOk: z.boolean(),
  note: z.string().nullable(),
  prices: z.record(z.string(), Money.nullable()),
  /** Сумма по позициям с ценой + доставка */
  total: Money,
  missing: z.number().int(),
  /** Отклонение от сметы по позициям, где есть обе цены: + дороже, − дешевле */
  vsEstimatePct: z.number().nullable(),
  isCheapest: z.boolean(),
  isSelected: z.boolean(),
  /** Позиции сверх запроса и их сумма (ЗК-31) */
  extras: z.array(OfferExtra.extend({ note: z.string().nullable() })),
  extrasTotal: Money.nullable(),
});
export type OfferView = z.infer<typeof OfferView>;

/**
 * Правило трёх КП (ЗК-05) для карточки заявки: видно до выбора и согласования. Сумма и порог — по уровню
 * денег; без него видно только, нужны ли три КП и выполнено ли правило.
 */
export const ThreeOffersCheck = z.object({
  /** Порог компании, ₸; null — суммы скрыты */
  limit: Money.nullable(),
  /** По чему сравнивается: сумма выбранного КП (будущий договор), иначе смета; none — суммы ещё нет */
  basis: z.enum(['offer', 'estimate', 'none']),
  amount: Money.nullable(),
  /** Нужны три КП или обоснование; null — не известно, пока нет сметы и выбранного КП */
  required: z.boolean().nullable(),
  offers: z.number().int(),
  reasonFilled: z.boolean(),
  /** Правило выполнено: не нужно, или КП не меньше трёх, или есть обоснование */
  ok: z.boolean(),
});
export type ThreeOffersCheck = z.infer<typeof ThreeOffersCheck>;

/** Сходная закупка, с которой эта вместе выше порога (ЗК-33) */
export const SplitMatch = z.object({
  kind: z.enum(['contract', 'request']),
  id: z.uuid(),
  number: z.string(),
  /** Дата создания */
  at: z.string(),
  amount: Money.nullable(),
});
export type SplitMatch = z.infer<typeof SplitMatch>;

/**
 * Возможное дробление закупки (ЗК-33): закупка до порога трёх КП, а у того же контрагента за окно есть
 * сходные — и вместе они выше порога. В заявке до выбора КП считается для каждого КП: видно до решения.
 */
export const SplitView = z.object({
  /** КП заявки, для которого посчитано; null — договор или выбранный КП */
  offerId: z.uuid().nullable(),
  supplierName: z.string(),
  windowDays: z.number().int(),
  limit: Money.nullable(),
  count: z.number().int(),
  /** Вместе с этой закупкой; null — суммы скрыты */
  total: Money.nullable(),
  /** Сходные закупки, которые человек вправе открыть */
  matches: z.array(SplitMatch),
  /** Сколько сходных закупок в проектах, к которым у человека нет доступа */
  hidden: z.number().int(),
});
export type SplitView = z.infer<typeof SplitView>;

export const PurchaseRequestDetail = PurchaseRequestSummary.extend({
  /** Подписанный договор по заявке: заявка на оплату закрывает его плановый платёж (ФН-02) */
  signedContractId: z.uuid().nullable(),
  budgetLineId: z.uuid().nullable(),
  budgetLineName: z.string().nullable(),
  note: z.string().nullable(),
  source: z.string().nullable(),
  selectedOfferId: z.uuid().nullable(),
  selectionReason: z.string().nullable(),
  noThreeOffersReason: z.string().nullable(),
  returnReason: z.string().nullable(),
  items: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      unit: z.string(),
      quantity: z.number(),
      estimatePrice: Money.nullable(),
      note: z.string().nullable(),
      bestPrice: Money.nullable(),
      /** Взято со склада под эту строку (СК-15, поток 5): действующие резервы и ожидающие согласия владельца склада */
      fromStock: z.number(),
      /** Ждёт согласия владельца другого склада */
      fromStockRequested: z.number(),
    }),
  ),
  offerList: z.array(OfferView),
  events: z.array(
    z.object({
      id: z.uuid(),
      fromStage: z.string().nullable(),
      toStage: z.string(),
      comment: z.string().nullable(),
      actorName: z.string().nullable(),
      at: z.string(),
    }),
  ),
  /** Статья и остаток утверждённого финансового плана (ЗК-29) */
  finCheck: z
    .object({
      /** Номер действующего ФП; null — утверждённого ФП нет */
      planNumber: z.number().int().nullable(),
      article: z.string().nullable(),
      /** Суммы ФП видны с уровнем «Все деньги» и правом на бюджет или финансы проекта плана */
      plan: z.number().nullable(),
      contracted: z.number().nullable(),
      paid: z.number().nullable(),
      /** Остаток для этой заявки: план − договоры − резерв других заявок без договора */
      remainder: z.number().nullable(),
      requestAmount: z.number().nullable(),
      /** Сумма заявки по смете (до выбора) или по выбранному КП (после выбора) */
      basis: z.enum(['estimate', 'offer']),
      /** Заявка без статьи или без сметной суммы: дальше проверки не идёт */
      blocked: z.boolean(),
      /** Сумма выше остатка статьи: сбор КП идёт, договор — после новой версии ФП (ЗК-29) */
      over: z.boolean(),
    })
    .nullable(),
  /** Веса критериев (шаблон или изменённые с обоснованием, ЗК-21) */
  weights: z.record(z.string(), z.number()),
  weightsChanged: z.boolean(),
  weightsReason: z.string().nullable(),
  /** Правило трёх КП (ЗК-05) — до выбора и согласования */
  threeOffers: ThreeOffersCheck,
  /** Возможное дробление (ЗК-33): по выбранному КП или, до выбора, по каждому КП */
  splits: z.array(SplitView),
  /** Что мешает идти дальше: меньше трёх КП, нет обоснования выбора и т. п. */
  warnings: z.array(z.string()),
});
export type PurchaseRequestDetail = z.infer<typeof PurchaseRequestDetail>;

/* ───────────────────────── Реестры и заявки на оплату (ЗК-01, ЗК-14…16) ───────────────────────── */

export const PaymentRequestView = z.object({
  id: z.uuid(),
  number: z.string(),
  projectId: z.uuid(),
  projectCode: z.string(),
  requestId: z.uuid().nullable(),
  requestNumber: z.string().nullable(),
  requestTitle: z.string().nullable(),
  contractId: z.uuid().nullable(),
  contractNumber: z.string().nullable(),
  registryId: z.uuid().nullable(),
  registryNumber: z.string().nullable(),
  /** Реестр передан в финансы: до этого заявку по нему на оплату не передать (ЗК-01) */
  registrySent: z.boolean(),
  counterpartyName: z.string().nullable(),
  kind: z.enum(Object.keys(PAYMENT_KINDS) as [PaymentKind, ...PaymentKind[]]),
  amount: Money.nullable(),
  plannedDate: DateStr.nullable(),
  costArticle: z.string().nullable(),
  status: z.enum(Object.keys(PAYMENT_REQUEST_STATUSES) as [PaymentRequestStatus, ...PaymentRequestStatus[]]),
  overReason: z.string().nullable(),
  overApprovedByName: z.string().nullable(),
  overApprovedAt: z.string().nullable(),
  oneCNumber: z.string().nullable(),
  oneCDate: DateStr.nullable(),
  oneCStatus: z.string().nullable(),
  sentAt: z.string().nullable(),
  paidAmount: Money.nullable(),
  paidOn: DateStr.nullable(),
  cancelReason: z.string().nullable(),
  note: z.string().nullable(),
  /** Плановая дата прошла, а факта нет */
  overdue: z.boolean(),
  /** Плановый платёж договора, который закрывает заявка (ФН-02) */
  scheduleRowId: z.uuid().nullable(),
  scheduleLabel: z.string().nullable(),
  priority: z.enum(Object.keys(PAYMENT_PRIORITIES) as [PaymentPriority, ...PaymentPriority[]]),
  fundingSource: z.enum(Object.keys(FUNDING_SOURCES) as [FundingSource, ...FundingSource[]]).nullable(),
  /** Счёт поставщика и назначение платежа (ФН-21) */
  invoiceNumber: z.string().nullable(),
  invoiceDate: DateStr.nullable(),
  purpose: z.string().nullable(),
  /**
   * Остановка до решения финансового менеджера: возможная повторная оплата (ФН-21) или платёж сверх
   * лимита дня или недели (ФН-02)
   */
  hold: z
    .object({
      reason: z.string(),
      kinds: z.array(z.enum(['duplicate', 'limit', 'match'])),
      decision: z.string().nullable(),
      decidedByName: z.string().nullable(),
      decidedAt: z.string().nullable(),
    })
    .nullable(),
  /**
   * Похожие оплаты того же контрагента на ту же сумму: заявки с тем же счётом или назначением (старые
   * заявки без счёта и назначения — по тому же договору) и оплаты, внесённые без заявки. Номер и проект
   * скрыты, если проект человеку не виден.
   */
  duplicates: z.array(
    z.object({
      id: z.uuid(),
      kind: z.enum(['request', 'fact']),
      number: z.string().nullable(),
      status: z.string(),
      projectCode: z.string().nullable(),
      date: DateStr.nullable(),
    }),
  ),
  /** Позиции счёта поставщика и результат сверки с договором и приходом (ФН-20) */
  invoiceLines: z.array(PaymentInvoiceLineView),
  match: MatchResult.nullable(),
  /** Удержан перерасход давальческих подрядчика (СК-24): сумма к оплате уже уменьшена на него */
  excessWithheld: Money.nullable(),
  /** Выгружена в 1С файлом или обменом (ИН-05) */
  exportedAt: z.string().nullable(),
  /** Переносы плановой даты с причинами */
  moves: z.array(
    z.object({
      from: DateStr.nullable(),
      to: DateStr.nullable(),
      reason: z.string(),
      byName: z.string().nullable(),
      at: z.string(),
    }),
  ),
  /** Комплект документов по справочнику: вид договора и тип платежа (ФН-19, ДГ-12) */
  docs: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      ok: z.boolean(),
      /** Отмечен сам: «подписанный договор» — по подписанному договору */
      auto: z.boolean(),
      byName: z.string().nullable(),
      at: z.string().nullable(),
      files: z.array(z.object({ id: z.uuid(), name: z.string() })),
    }),
  ),
  docsComplete: z.boolean(),
  /** Кто готовил и кто передал на оплату: остановку снимает другой человек */
  createdById: z.uuid().nullable(),
  sentById: z.uuid().nullable(),
  /** Документ считается полученным, только когда к нему приложен файл (настройка финансового менеджера) */
  docsRequireFile: z.boolean(),
  /** Файлы заявки без вида документа: приложенные до справочника комплектов */
  otherFiles: z.array(z.object({ id: z.uuid(), name: z.string() })),
  createdAt: z.string(),
});
export type PaymentRequestView = z.infer<typeof PaymentRequestView>;

export const PaymentRequestInput = z
  .object({
    requestId: z.uuid().nullable().optional(),
    contractId: z.uuid().nullable().optional(),
    kind: z.enum(Object.keys(PAYMENT_KINDS) as [PaymentKind, ...PaymentKind[]]),
    amount: MoneyIn.positive('Сумма больше нуля'),
    plannedDate: DateStr.nullable().optional(),
    note: z.string().trim().max(1000).nullable().optional(),
    /** Плановый платёж из графика договора, который закрывает заявка (ФН-02) */
    scheduleRowId: z.uuid().nullable().optional(),
    priority: z.enum(Object.keys(PAYMENT_PRIORITIES) as [PaymentPriority, ...PaymentPriority[]]).optional(),
    fundingSource: z
      .enum(Object.keys(FUNDING_SOURCES) as [FundingSource, ...FundingSource[]])
      .nullable()
      .optional(),
    invoiceNumber: z.string().trim().max(60).nullable().optional(),
    invoiceDate: DateStr.nullable().optional(),
    purpose: z.string().trim().max(500).nullable().optional(),
    /**
     * Удержать перерасход давальческих подрядчика (СК-24): сумма, которую человек видел в форме. Сумма
     * заявки — уже за вычетом удержания; сервер пересчитывает перерасход и отказывает, если он изменился
     */
    withholdExcess: MoneyIn.positive().optional(),
  })
  .refine((v) => v.requestId || v.contractId, { message: 'Укажите заявку на закуп или договор' });
export type PaymentRequestInput = z.infer<typeof PaymentRequestInput>;

/**
 * Перерасход давальческих материалов подрядчика по договору работ или субподряда (СК-24): по утверждённым
 * руководителем проекта отчётам — к удержанию при оплате, по нерешённым — только предупреждение. Уже
 * удержанное другой заявкой на оплату не предлагается. Суммы — с уровнем денег «Акты и счета».
 */
export const ContractorExcessView = z.object({
  /** Договор работ или субподряда: у других договоров давальческих нет */
  applicable: z.boolean(),
  approvedAmount: Money.nullable(),
  pendingAmount: Money.nullable(),
  /** У части утверждённого перерасхода нет цены на складе подрядчика — его сумма не посчитана */
  unpriced: z.boolean(),
  lines: z.array(
    z.object({
      documentId: z.uuid(),
      state: z.enum(['approved', 'pending']),
      itemName: z.string(),
      unit: z.string().nullable(),
      excess: z.number(),
      amount: Money.nullable(),
    }),
  ),
});
export type ContractorExcessView = z.infer<typeof ContractorExcessView>;

export const PaymentRequestPatch = z.object({
  kind: z.enum(Object.keys(PAYMENT_KINDS) as [PaymentKind, ...PaymentKind[]]).optional(),
  amount: MoneyIn.positive().optional(),
  plannedDate: DateStr.nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
  scheduleRowId: z.uuid().nullable().optional(),
  priority: z.enum(Object.keys(PAYMENT_PRIORITIES) as [PaymentPriority, ...PaymentPriority[]]).optional(),
  fundingSource: z
    .enum(Object.keys(FUNDING_SOURCES) as [FundingSource, ...FundingSource[]])
    .nullable()
    .optional(),
  /** Перенос плановой даты — только с причиной (ФН-02) */
  moveReason: z.string().trim().min(3).max(500).optional(),
  invoiceNumber: z.string().trim().max(60).nullable().optional(),
  invoiceDate: DateStr.nullable().optional(),
  purpose: z.string().trim().max(500).nullable().optional(),
  /** Ручные поля 1С, этап 1 (ЗК-16) */
  oneCNumber: z.string().trim().max(60).nullable().optional(),
  oneCDate: DateStr.nullable().optional(),
  oneCStatus: z.string().trim().max(120).nullable().optional(),
});
export type PaymentRequestPatch = z.infer<typeof PaymentRequestPatch>;

export const PaymentRequestAction = z.object({
  /** approve — прежнее согласование сверх договора: больше не применяется (ЗК-15);
   * release — финансовый менеджер снимает остановку по повторной оплате с причиной (ФН-21);
   * recheck — повторная сверка счёта с договором и приходом после допсоглашения или правки прихода (ФН-20) */
  action: z.enum(['send', 'approve', 'release', 'pay', 'cancel', 'recheck']),
  /** Причина отмены (cancel) или решения по повторной оплате (release) */
  reason: z.string().trim().max(2000).optional(),
  paidAmount: MoneyIn.positive().optional(),
  paidOn: DateStr.optional(),
});
export type PaymentRequestAction = z.infer<typeof PaymentRequestAction>;

export const RegistryRequestRow = z.object({
  id: z.uuid(),
  number: z.string(),
  title: z.string(),
  projectId: z.uuid(),
  projectCode: z.string(),
  stage: ProcurementStageSchema,
  supplier: z.string().nullable(),
  contractNumber: z.string().nullable(),
  costArticle: z.string().nullable(),
  dueAmount: Money.nullable(),
  paidAmount: Money.nullable(),
});
export type RegistryRequestRow = z.infer<typeof RegistryRequestRow>;

export const RegistryView = z.object({
  id: z.uuid(),
  number: z.string(),
  legalEntityId: z.uuid(),
  legalEntityName: z.string(),
  status: z.enum(Object.keys(REGISTRY_STATUSES) as [RegistryStatus, ...RegistryStatus[]]),
  note: z.string().nullable(),
  sentAt: z.string().nullable(),
  sentByName: z.string().nullable(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  /** Число заявок и общая сумма по связанным карточкам (ЗК-14) */
  count: z.number().int(),
  total: Money.nullable(),
  paid: Money.nullable(),
  requests: z.array(RegistryRequestRow),
});
export type RegistryView = z.infer<typeof RegistryView>;

export const RegistryInput = z.object({
  legalEntityId: z.uuid(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type RegistryInput = z.infer<typeof RegistryInput>;

/** Экран руководителя закупок без открытия карточек (ЗК-12) */
export const ProcurementOverview = z.object({
  counts: z.object({
    open: z.number().int(),
    fresh: z.number().int(),
    rfq: z.number().int(),
    analysis: z.number().int(),
    approval: z.number().int(),
    signing: z.number().int(),
    readyForRegistry: z.number().int(),
    inRegistry: z.number().int(),
    onPayment: z.number().int(),
    paid: z.number().int(),
  }),
  overdue: z.object({
    rfq: z.number().int(),
    approval: z.number().int(),
    signing: z.number().int(),
    registry: z.number().int(),
    payment: z.number().int(),
  }),
  /** null — суммы скрыты уровнем доступа к деньгам */
  money: z
    .object({
      activeRequests: Money,
      contracts: Money,
      sentToPayment: Money,
      paid: Money,
      remainder: Money,
    })
    .nullable(),
  overduePayments: z.array(PaymentRequestView),
});
export type ProcurementOverview = z.infer<typeof ProcurementOverview>;
